/*
 * schema.org structured data builders.
 *
 * Kept out of the page components so every page emits consistent markup, and so
 * there is one place to check when Search Console complains.
 *
 * Rule that matters: structured data must describe what is actually visible on
 * the page. Claiming an author, date or rating the reader cannot see is a
 * spam signal, not a ranking boost.
 */

import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION,
  SITE_AUTHOR,
  SOCIAL_IMAGE,
  SOCIAL_LINKS,
} from '../constants/site';
import { blogPath } from './slug';
import { isoDate, metaDescription, modifiedAt, publishedAt, wordCount } from './post';

const abs = (pathOrUrl) =>
  !pathOrUrl ? undefined : pathOrUrl.startsWith('http') ? pathOrUrl : `${SITE_URL}${pathOrUrl}`;

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;

/** Publisher identity. Referenced by @id from the other schemas. */
export const organisationSchema = () => {
  const sameAs = SOCIAL_LINKS.map((s) => s.href).filter(Boolean);
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: SITE_URL,
    description: SITE_DESCRIPTION,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo192.png`,
      width: 192,
      height: 192,
    },
    ...(sameAs.length ? { sameAs } : {}),
  };
};

/**
 * Site-level schema with a SearchAction, which is what makes a sitelinks
 * search box eligible in Google results.
 */
export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': SITE_ID,
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESCRIPTION,
  publisher: { '@id': ORG_ID },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/blogs?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

/** Article page. `crumbs` is [{name, path}] matching the visible breadcrumb. */
export const articleSchema = (post) => {
  const path = blogPath(post);
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}${path}#article`,
    headline: post.title,
    description: metaDescription(post),
    image: [abs(post.image) || `${SITE_URL}${SOCIAL_IMAGE}`],
    author: {
      '@type': 'Person',
      name: post.author || SITE_AUTHOR,
    },
    publisher: { '@id': ORG_ID },
    datePublished: isoDate(publishedAt(post)),
    dateModified: isoDate(modifiedAt(post)),
    articleSection: post.category,
    keywords: (post.tags || []).join(', ') || undefined,
    wordCount: wordCount(post) || undefined,
    inLanguage: 'en',
    isPartOf: { '@id': SITE_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}${path}` },
  };
};

/** Breadcrumb trail. Must match the visible <nav> exactly. */
export const breadcrumbSchema = (crumbs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: `${SITE_URL}${c.path}`,
  })),
});

/**
 * Listing page (home / all articles / a category). The ItemList tells Google
 * these are links to distinct articles rather than the page's own content.
 */
export const collectionSchema = ({ name, description, path, posts = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${SITE_URL}${path}#collection`,
  name,
  description,
  url: `${SITE_URL}${path}`,
  isPartOf: { '@id': SITE_ID },
  publisher: { '@id': ORG_ID },
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems: posts.length,
    itemListElement: posts.map((post, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}${blogPath(post)}`,
      name: post.title,
    })),
  },
});
